import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ABTestVariant } from '@/hooks/useABTestVariants';
import { ThumbsUp, Star, Trophy, Download, Loader2, Trash2 } from 'lucide-react';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface ComparisonGridProps {
  variants: ABTestVariant[];
  onVote: (variantId: string) => Promise<void>;
  onSetWinner: (variantId: string) => Promise<void>;
  onDeleteVariant?: (variantId: string) => Promise<void>;
  isLoading?: boolean;
}

export const ComparisonGrid = ({ variants, onVote, onSetWinner, onDeleteVariant, isLoading }: ComparisonGridProps) => {
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [votingFor, setVotingFor] = useState<string | null>(null);
  const [settingWinner, setSettingWinner] = useState<string | null>(null);
  const [deletingVariant, setDeletingVariant] = useState<string | null>(null);
  const [variantToDelete, setVariantToDelete] = useState<string | null>(null);
  const [imageUrls, setImageUrls] = useState<Record<string, string>>({});

  useEffect(() => {
    const loadUrls = async () => {
      const idsToFetch = variants
        .map(v => v.image_id)
        .filter((id): id is string => !!id && !imageUrls[id]);
      for (const id of idsToFetch) {
        const { data, error } = await supabase
          .from('generated_images')
          .select('image_url')
          .eq('id', id)
          .single();
        if (!error && data?.image_url) {
          setImageUrls(prev => ({ ...prev, [id]: data.image_url }));
        }
      }
    };
    loadUrls();
  }, [variants]);

  const handleVote = async (variantId: string) => {
    setVotingFor(variantId);
    try {
      await onVote(variantId);
    } finally {
      setVotingFor(null);
    }
  };

  const handleSetWinner = async (variantId: string) => {
    setSettingWinner(variantId);
    try {
      await onSetWinner(variantId);
    } finally {
      setSettingWinner(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!variantToDelete || !onDeleteVariant) return;
    
    setDeletingVariant(variantToDelete);
    try {
      await onDeleteVariant(variantToDelete);
      toast.success('Variant deleted successfully');
      setVariantToDelete(null);
    } catch (error) {
      toast.error('Failed to delete variant');
    } finally {
      setDeletingVariant(null);
    }
  };

  if (variants.length === 0) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-400">No variants generated yet</p>
      </div>
    );
  }

  return (
    <>
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
        {variants.map((variant) => (
          <Card key={variant.id} className="bg-white/5 border-white/10 overflow-hidden hover:border-blue-500/50 transition-all">
            {/* Image Preview */}
            <div
              className="relative aspect-square bg-gray-800 cursor-pointer group"
              onClick={() => variant.image_id && setSelectedImage(imageUrls[variant.image_id] || null)}
            >
              {variant.image_id ? (
                <>
                  <img
                    src={variant.image_id ? (imageUrls[variant.image_id] || '/placeholder.svg') : '/placeholder.svg'}
                    alt={variant.variant_name}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/40 transition-all flex items-center justify-center">
                    <span className="text-white opacity-0 group-hover:opacity-100 transition-opacity">
                      Click to enlarge
                    </span>
                  </div>
                </>
              ) : (
                <div className="flex items-center justify-center h-full">
                  <Loader2 className="w-8 h-8 text-gray-400 animate-spin" />
                </div>
              )}

              {/* Winner Badge */}
              {variant.is_winner && (
                <div className="absolute top-3 right-3">
                  <Badge className="bg-yellow-500 text-black">
                    <Trophy className="w-3 h-3 mr-1" />
                    Winner
                  </Badge>
                </div>
              )}
            </div>

            {/* Variant Info */}
            <div className="p-4 space-y-3">
              <div>
                <h3 className="text-white font-semibold mb-2">{variant.variant_name}</h3>
                <div className="flex flex-wrap gap-2">
                  {variant.variant_config.model && (
                    <Badge variant="outline" className="text-xs">
                      {variant.variant_config.model}
                    </Badge>
                  )}
                  {variant.variant_config.size && (
                    <Badge variant="outline" className="text-xs">
                      {variant.variant_config.size}
                    </Badge>
                  )}
                  {variant.variant_config.style && (
                    <Badge variant="outline" className="text-xs">
                      {variant.variant_config.style}
                    </Badge>
                  )}
                </div>
              </div>

              {/* Voting Section */}
              <div className="flex items-center justify-between pt-3 border-t border-white/10">
                <div className="flex items-center space-x-2">
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => handleVote(variant.id)}
                    disabled={votingFor === variant.id || isLoading}
                    className="flex items-center space-x-1"
                  >
                    {votingFor === variant.id ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <ThumbsUp className="w-4 h-4" />
                    )}
                    <span>{variant.votes}</span>
                  </Button>
                  <div className="flex items-center space-x-1 text-yellow-400">
                    <Star className="w-4 h-4 fill-current" />
                    <span className="text-sm">{variant.performance_score || 0}</span>
                  </div>
                </div>

                <Button
                  size="sm"
                  onClick={() => handleSetWinner(variant.id)}
                  disabled={variant.is_winner || settingWinner === variant.id || isLoading}
                  className={`${
                    variant.is_winner
                      ? 'bg-yellow-600 hover:bg-yellow-700'
                      : 'bg-blue-600 hover:bg-blue-700'
                  }`}
                >
                  {settingWinner === variant.id ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : variant.is_winner ? (
                    <>
                      <Trophy className="w-4 h-4 mr-1" />
                      Winner
                    </>
                  ) : (
                    'Set Winner'
                  )}
                </Button>
              </div>

              {/* Download Button */}
              {variant.image_id && (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full"
                  onClick={() => {
                    // Download logic here
                    console.log('Download variant:', variant.id);
                  }}
                >
                  <Download className="w-4 h-4 mr-2" />
                  Download
                </Button>
              )}
              
              {/* Delete Button */}
              {onDeleteVariant && (
                <Button
                  size="sm"
                  variant="outline"
                  className="w-full text-red-400 hover:text-red-300 hover:bg-red-500/10 border-red-500/20"
                  onClick={() => setVariantToDelete(variant.id)}
                  disabled={deletingVariant === variant.id}
                >
                  {deletingVariant === variant.id ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <Trash2 className="w-4 h-4 mr-2" />
                  )}
                  Delete Variant
                </Button>
              )}
            </div>
          </Card>
        ))}
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!variantToDelete} onOpenChange={() => setVariantToDelete(null)}>
        <AlertDialogContent className="bg-gray-900 border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Variant?</AlertDialogTitle>
            <AlertDialogDescription className="text-gray-400">
              This action cannot be undone. This will permanently delete the variant and its associated data.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-white/5 text-white border-white/10 hover:bg-white/10">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteConfirm}
              className="bg-red-600 hover:bg-red-700 text-white"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Fullscreen Image Modal */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-4xl bg-black/90 border-white/10">
          <DialogHeader className="sr-only">
            <DialogTitle>Variant Preview</DialogTitle>
            <DialogDescription>Full size variant image preview</DialogDescription>
          </DialogHeader>
          <img
            src={selectedImage || '/placeholder.svg'}
            alt="Variant preview"
            className="w-full h-auto"
          />
        </DialogContent>
      </Dialog>
    </>
  );
};
